import { Divider, LinearProgress, Stack, Typography } from "@material-ui/core";
import { SentMessageRow } from "components/_dashboard/contacts/table/rows/ContactTableNestedRowSentMessages";
import { db } from "firebaseConfig";
import useAuth from "hooks/useAuth";
import useContactsLookup from "hooks/contacts/useContactsLookup";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { GenericConverter } from "utils/firestoreConverters";
import type { BulkMessageSentMessage } from "../../../../../@types/sharedSchema";

type ListingDetailsMessagesTabProps = {
  listingId: string;
};

export function ListingDetailsMessagesTab({
  listingId
}: ListingDetailsMessagesTabProps): JSX.Element {
  const [sentMessages, setSentMessages] = useState<BulkMessageSentMessage[]>([]);
  const [loading, setLoading] = useState(true);

  const {
    user: { email }
  } = useAuth();

  const { t: translate } = useTranslation();

  useEffect(() => {
    const getListingSentMessages = async (): Promise<void> => {
      try {
        setLoading(true);

        const snapshot = await db
          .collection("dashboard-users-v2")
          .doc(email)
          .collection("sentMessages")
          .where("listingId", "==", listingId)
          .withConverter(GenericConverter<BulkMessageSentMessage>())
          .get();

        const data = snapshot.docs.map((doc) => doc.data());

        setSentMessages(data);
      } catch (error) {
        console.error("Error fetching sent messages:", error);
      } finally {
        setLoading(false);
      }
    };

    void getListingSentMessages();
  }, [email, listingId]);

  // Each row says who its message went to. One lookup for every recipient in the list,
  // handed down to the rows.
  const recipients = useMemo(() => sentMessages.map(({ sentTo }) => sentTo), [sentMessages]);
  const { lookup } = useContactsLookup(recipients);

  if (loading) return <LinearProgress />;

  if (!sentMessages.length) {
    return (
      <Typography variant="body1">{translate("listingsPage.error.noMessagesSentFound")}</Typography>
    );
  }

  return (
    <Stack divider={<Divider />}>
      {sentMessages.map((sentMessage) => (
        <SentMessageRow inListingPage lookup={lookup} sentMessage={sentMessage} key={sentMessage.id} />
      ))}
    </Stack>
  );
}
